import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { CONFIG } from 'src/global-config';

import { Form, Field, schemaUtils } from 'src/components/hook-form';
import { useColorScheme } from '@mui/material';
import { useLocale } from 'src/locales';
// import { serviceFormSchema } from 'src/utils/validations';
import { useBusiness } from 'src/api/business/useBusiness';
import { useEffect } from 'react';
import { notify } from 'src/utils/helper';
import { createSchemas } from 'src/utils/use-validations';

// ----------------------------------------------------------------------

const serviceOptions = [
  { value: 'Quotation', label: 'Quotation' },
  { value: 'Transportation Contract', label: 'Transportation_Contract' },
];

export function ContactFormInfo({ sx, ...other }) {
  const { t } = useLocale();
  const defaultValues = {
    company: '',
    mobile: '',
    service: '',
    email: '',
  };

  const { serviceFormSchema } = createSchemas();
  const { data: postContactData, mutate: submitContactForm, isPending } = useBusiness();

  const methods = useForm({
    resolver: zodResolver(serviceFormSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (postContactData) {
      if (postContactData?.success) {
        notify('success', 'Your request has been submitted successfully');
        reset();
      } else {
        notify('error', 'There was an error submitting your request');
      }
    }
  }, [postContactData, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      submitContactForm({ ...data, service: data?.service?.value });
    } catch (error) {
      console.error(error);
    }
  });

  const getNameForLanguage = (key) => {
    return key;
  };

  const renderImage = () => (
    <Box
      component="img"
      alt={t('contact-img2-title')}
      title={t('contact-img2-meta-desk')}
      src={`${CONFIG.assetsDir}/assets/images/home/seo/Contact-Us/shahen-contact-form.webp`}
      sx={{
        display: 'block',
        width: '100%',
        height: 480,
        borderRadius: 2,
        maxWidth: { xs: 300, sm: 400, md: 480 },
        mx: 'auto',
        maxHeight: { xs: 200, sm: 300, md: 480 },
        objectFit: 'cover',
      }}
    />
  );

  const renderTexts = () => (
    <>
      <Typography variant="h3" sx={{ fontSize: '2rem !important' }}>
        {t('Drop_us_a_line')}
      </Typography>
      <Typography sx={{ mt: 2, mb: 5, color: 'text.secondary' }}>
        {t('We_normally_respond_within_two_business_days')}
      </Typography>
    </>
  );

  const renderForm = () => (
    <Box sx={{ gap: 2.5, display: 'flex', flexDirection: 'column' }}>
      <Field.Text name="company" label={t('Enter_Company_Name')} />
      <Field.Text name="mobile" label={t('Enter_Mobile_Number')} />
      <Field.Text name="email" label={t('Enter_Email_Address')} />
      <Field.Select
        name="service"
        options={serviceOptions}
        label={t('Service_Needed')}
        placeholder={t('Service_Needed')}
        labelKey={getNameForLanguage('label')}
        sx={{ flex: 1 }}
      />

      <Button
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting || isPending}
        color="primary"
        sx={{ mr: 'auto', ml: { xs: 'auto', md: 'unset' }, bgcolor: 'primary.main' }}
      >
        {t('Send_Request')}
      </Button>
    </Box>
  );
  const { mode } = useColorScheme();
  return (
    <Box
      component="section"
      sx={[
        {
          py: { xs: 5, md: 5 },
          bgcolor: mode === 'light' ? 'white' : 'background.neutral',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Container>
        <Grid container spacing={3} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Grid size={{ xs: 12, md: 6, lg: 5 }}>{renderImage()}</Grid>

          <Grid size={{ xs: 12, md: 6, lg: 6 }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            {renderTexts()}

            <Form methods={methods} onSubmit={onSubmit}>
              {renderForm()}
            </Form>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
